import React, { useState } from 'react';
import { X, Loader, Plus, Handshake, Trash2 } from 'lucide-react';

const SupplierModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  supplier = null, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: supplier?.name || '',
    contactName: supplier?.contactName || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    address: supplier?.address || '',
    categories: supplier?.categories || '',
    notes: supplier?.notes || '',
    onTimeDelivery: supplier?.onTimeDelivery || 100,
    active: supplier?.active !== undefined ? supplier.active : true
  });

  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del proveedor es obligatorio';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    if (formData.onTimeDelivery < 0 || formData.onTimeDelivery > 100) {
      newErrors.onTimeDelivery = 'El porcentaje debe estar entre 0 y 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete && supplier) {
      onDelete(supplier.id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const isEditMode = !!supplier;

  return (
    <div className="add-product-overlay" onClick={handleOverlayClick}>
      <div className="add-product-form supplier-form" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <div className="form-title-section">
            <div className="form-icon">
              <Handshake size={24} />
            </div>
            <h4>{isEditMode ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h4>
          </div>
          <button 
            className="close-form-btn"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="form-content">
          {/* Basic Information */}
          <div className="form-section">
            <h5 className="section-title">Información Básica</h5>
            
            <div className="form-row">
              <div className="form-group">
                <label>Nombre del Proveedor *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Nombre del proveedor"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  disabled={isLoading}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label>Persona de Contacto</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => handleChange('contactName', e.target.value)}
                  placeholder="Nombre del contacto"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="011-1234-5678"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contacto@proveedor.com"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  disabled={isLoading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Dirección completa"
                className="form-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Business Information */}
          <div className="form-section">
            <h5 className="section-title">Información Comercial</h5>
            
            <div className="form-row">
              <div className="form-group">
                <label>Categorías de Productos</label>
                <input
                  type="text"
                  value={formData.categories}
                  onChange={(e) => handleChange('categories', e.target.value)}
                  placeholder="Pan,Facturas,Salados (separadas por comas)"
                  className="form-input"
                  disabled={isLoading}
                />
                <small className="form-help">
                  Categorías que suministra, separadas por comas
                </small>
              </div>
              
              <div className="form-group">
                <label>Puntualidad de Entrega (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.onTimeDelivery}
                  onChange={(e) => handleChange('onTimeDelivery', parseInt(e.target.value) || 0)}
                  className={`form-input ${errors.onTimeDelivery ? 'error' : ''}`}
                  disabled={isLoading}
                />
                {errors.onTimeDelivery && <span className="error-message">{errors.onTimeDelivery}</span>}
                <small className="form-help">
                  Porcentaje estimado de entregas puntuales
                </small>
              </div>
            </div>

            <div className="form-group">
              <label>Notas</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Notas adicionales sobre el proveedor (horarios, condiciones especiales, etc.)"
                className="form-textarea"
                rows="3"
                disabled={isLoading}
              />
            </div>

            {/* Status toggle for edit mode */}
            {isEditMode && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => handleChange('active', e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="checkbox-custom"></span>
                  Proveedor activo
                </label>
                <small className="form-help">
                  Los proveedores inactivos no aparecerán en las nuevas órdenes de compra
                </small>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            {isEditMode && (
              <button 
                type="button"
                className="btn-danger"
                onClick={handleDeleteClick}
                disabled={isLoading}
              >
                <Trash2 size={16} />
                Eliminar Proveedor
              </button>
            )}
            <div className="form-actions-right">
              <button 
                type="button"
                className="btn-secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                type="submit"
                className="btn-primary"
                disabled={isLoading || !formData.name.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader size={16} className="spinning" />
                    {isEditMode ? 'Guardando...' : 'Creando...'}
                  </>
                ) : (
                  <>
                    {isEditMode ? (
                      <>
                        <Plus size={16} />
                        Guardar Cambios
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Crear Proveedor
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
        
        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="delete-confirmation-overlay">
            <div className="delete-confirmation-dialog">
              <div className="delete-confirmation-header">
                <h4>¿Eliminar proveedor {formData.name}?</h4>
              </div>
              <div className="delete-confirmation-content">
                <p>Esta acción eliminará permanentemente el proveedor y no se puede deshacer.</p>
                <p><strong>Nota:</strong> No se puede eliminar si tiene órdenes de compra activas o pendientes.</p>
              </div>
              <div className="delete-confirmation-actions">
                <button 
                  type="button"
                  className="btn-secondary"
                  onClick={handleDeleteCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  className="btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="spinning" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Eliminar Definitivamente
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierModal;